import React, { useContext, useEffect, useRef, useState } from 'react'
import styles from './AdFormBasicInfo.module.css'
import { FormTitle } from '../../../Common/FormTitle'
import { FormField } from '../../../../Common/FormField'
import { NextButton } from '../../../Common/CtaButton/NextButton'
import { StepContext } from '../../StepContainer'
import {
    AGE_LIMITS,
    DATE_CONSTANTS,
    MONTHS,
    VALIDATION_MESSAGES,
} from '../../../../../constants/progressBanner'

const SelectArrow = () => (
    <svg
        width="10"
        height="10"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{
            position: 'absolute',
            top: '50%',
            right: '8px',
            transform: 'translateY(-50%) rotate(90deg)',
            pointerEvents: 'none',
        }}
    >
        <path
            d="M4.99986 15C4.74398 15 4.48797 14.9023 4.29297 14.707C3.90234 14.3164 3.90234 13.6836 4.29297 13.2929L9.58741 7.99992L4.29297 2.70705C3.90234 2.31642 3.90234 1.6836 4.29297 1.29297C4.6836 0.902343 5.31642 0.902343 5.70705 1.29297L11.7071 7.29304C12.0977 7.68367 12.0977 8.31649 11.7071 8.70712L5.70705 14.7072C5.51236 14.9031 5.25611 15 4.99986 15Z"
            fill="#B1AFAB"
            stroke="#B1AFAB"
            strokeLinejoin="round"
        />
    </svg>
)

const isKatakana = (text: string): boolean =>
    /^[\u30A0-\u30FF\u31F0-\u31FF]*$/.test(text)

const SEX_DISPLAY_MAP: Record<string, string> = {
    man: '男性',
    woman: '女性',
    other: 'その他',
}

export const AdFormBasicInfo = () => {
    const { formState } = useContext(StepContext)

    const isPrefilled = useRef({
        lastName: !!formState.lastName,
        firstName: !!formState.firstName,
        lastNameKana: !!formState.lastNameKana,
        firstNameKana: !!formState.firstNameKana,
    })

    // Name state
    const [lastName, setLastName] = useState(formState.lastName || '')
    const [firstName, setFirstName] = useState(formState.firstName || '')
    const [lastNameKana, setLastNameKana] = useState(formState.lastNameKana || '')
    const [firstNameKana, setFirstNameKana] = useState(
        formState.firstNameKana || ''
    )
    const [nameTouched, setNameTouched] = useState({
        lastName: false,
        firstName: false,
        lastNameKana: false,
        firstNameKana: false,
    })
    const [nameErrors, setNameErrors] = useState({
        lastName: '',
        firstName: '',
        lastNameKana: '',
        firstNameKana: '',
    })

    // Birthday state
    const getInitialYearAndMonth = () => {
        if (formState.birthday) {
            const [y, m] = formState.birthday.split('-')
            return { year: y || '', month: m ? String(parseInt(m, 10)) : '' }
        }
        return { year: '', month: '' }
    }
    const initialBirthday = getInitialYearAndMonth()
    const [year, setYear] = useState(initialBirthday.year)
    const [month, setMonth] = useState(initialBirthday.month)
    const [hasInteracted, setHasInteracted] = useState(false)
    const [birthdayValid, setBirthdayValid] = useState(false)
    const [birthdayError, setBirthdayError] = useState('')
    const [age, setAge] = useState<number | null>(null)

    // Sex state
    const [sex, setSex] = useState(formState.sex || '')

    const currentDate = new Date()
        .toISOString()
        .slice(0, DATE_CONSTANTS.DATE_SLICE_END)

    const birthday =
        year !== '' && month !== ''
            ? `${year}-${String(month).padStart(DATE_CONSTANTS.MONTH_START_PAD, '0')}-01`
            : null

    // Name validation
    useEffect(() => {
        setNameErrors({
            lastName:
                nameTouched.lastName && !lastName ? '入力してください' : '',
            firstName:
                nameTouched.firstName && !firstName ? '入力してください' : '',
            lastNameKana: nameTouched.lastNameKana
                ? !lastNameKana
                    ? '入力してください'
                    : !isKatakana(lastNameKana)
                      ? 'カタカナで入力してください'
                      : ''
                : '',
            firstNameKana: nameTouched.firstNameKana
                ? !firstNameKana
                    ? '入力してください'
                    : !isKatakana(firstNameKana)
                      ? 'カタカナで入力してください'
                      : ''
                : '',
        })
    }, [lastName, firstName, lastNameKana, firstNameKana, nameTouched])

    // Birthday validation
    useEffect(() => {
        if (!hasInteracted) {
            if (year !== '' || month !== '') setHasInteracted(true)
            return
        }

        if (!year || !month) {
            setBirthdayError(VALIDATION_MESSAGES.INVALID_VALUE)
            setBirthdayValid(false)
            return
        }

        const yearNum = parseInt(year, 10)
        const monthNum = parseInt(month, 10)

        if (isNaN(yearNum) || isNaN(monthNum)) {
            setBirthdayError(VALIDATION_MESSAGES.INVALID_NUMBER)
            setBirthdayValid(false)
            return
        }

        if (monthNum < MONTHS.MIN || monthNum > MONTHS.MAX) {
            setBirthdayError(VALIDATION_MESSAGES.INVALID_MONTH_RANGE)
            setBirthdayValid(false)
            return
        }

        const [currentYear, currentMonth] = currentDate.split('-').map(Number)

        if (
            yearNum > currentYear ||
            (yearNum === currentYear && monthNum > currentMonth)
        ) {
            setBirthdayError(VALIDATION_MESSAGES.INVALID_DATE)
            setBirthdayValid(false)
            return
        }

        const userAge = currentYear - yearNum
        if (userAge < AGE_LIMITS.MIN) {
            setBirthdayError(VALIDATION_MESSAGES.AGE_TOO_YOUNG)
            setBirthdayValid(false)
            return
        }
        if (userAge > AGE_LIMITS.MAX) {
            setBirthdayError(VALIDATION_MESSAGES.AGE_TOO_OLD)
            setBirthdayValid(false)
            return
        }

        setBirthdayError('')
        setBirthdayValid(true)

        const birthDate = new Date(yearNum, monthNum - 1)
        const today = new Date(currentDate)
        let calculatedAge = today.getFullYear() - birthDate.getFullYear()
        const monthDiff = today.getMonth() - birthDate.getMonth()
        if (
            monthDiff < 0 ||
            (monthDiff === 0 && today.getDate() < birthDate.getDate())
        ) {
            calculatedAge--
        }
        setAge(calculatedAge)
    }, [year, month, hasInteracted, currentDate])

    const handleNameBlur = (field: string) => {
        setNameTouched(prev => ({ ...prev, [field]: true }))
    }

    const handleYearChange = (value: string) => {
        setYear(value)
        if (!hasInteracted) setHasInteracted(true)
    }

    const handleMonthChange = (value: string) => {
        setMonth(value)
        if (!hasInteracted) setHasInteracted(true)
    }

    const currentYear = new Date().getFullYear()
    const yearOptions = Array.from(
        { length: currentYear - 1930 + 1 },
        (_, i) => currentYear - i
    )
    const monthOptions = Array.from({ length: 12 }, (_, i) => i + 1)

    const isNameValid =
        lastName !== '' &&
        firstName !== '' &&
        lastNameKana !== '' &&
        firstNameKana !== '' &&
        isKatakana(lastNameKana) &&
        isKatakana(firstNameKana)

    const isFormValid = isNameValid && birthdayValid && sex !== ''

    const sexDisplay = SEX_DISPLAY_MAP[sex] || ''

    return (
        <div className={styles.wrapper}>
            <div className={styles.selectionWrapper}>
                <FormTitle
                    formTitleText="あなたの名前は？"
                    dataViewLabel=""
                    dataClickLabel=""
                />

                {/* お名前 */}
                <div className={styles.section}>
                    <div className={styles.nameField}>
                        <div>
                            <FormField
                                label="姓"
                                placeholder="山田"
                                value={lastName}
                                onChange={setLastName}
                                onBlur={() => handleNameBlur('lastName')}
                                disabled={isPrefilled.current.lastName}
                            />
                            {nameErrors.lastName && (
                                <p className={styles.errorMessages}>
                                    {nameErrors.lastName}
                                </p>
                            )}
                        </div>
                        <div>
                            <FormField
                                label="名"
                                placeholder="太郎"
                                value={firstName}
                                onChange={setFirstName}
                                onBlur={() => handleNameBlur('firstName')}
                                disabled={isPrefilled.current.firstName}
                            />
                            {nameErrors.firstName && (
                                <p className={styles.errorMessages}>
                                    {nameErrors.firstName}
                                </p>
                            )}
                        </div>
                    </div>
                    <div className={styles.nameField}>
                        <div>
                            <FormField
                                label="セイ"
                                placeholder="ヤマダ"
                                value={lastNameKana}
                                onChange={setLastNameKana}
                                onBlur={() => handleNameBlur('lastNameKana')}
                                disabled={isPrefilled.current.lastNameKana}
                            />
                            {nameErrors.lastNameKana && (
                                <p className={styles.errorMessages}>
                                    {nameErrors.lastNameKana}
                                </p>
                            )}
                        </div>
                        <div>
                            <FormField
                                label="メイ"
                                placeholder="タロウ"
                                value={firstNameKana}
                                onChange={setFirstNameKana}
                                onBlur={() => handleNameBlur('firstNameKana')}
                                disabled={isPrefilled.current.firstNameKana}
                            />
                            {nameErrors.firstNameKana && (
                                <p className={styles.errorMessages}>
                                    {nameErrors.firstNameKana}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {/* 誕生月・性別 */}
                <div className={styles.birthdayGenderRow}>
                    <div className={styles.birthdayGenderSection}>
                        <p className={styles.sectionLabel}>誕生月</p>
                        <div className={styles.birthdayField}>
                            <div className={styles.selectWrapper}>
                                <select
                                    className={styles.yearSelect}
                                    value={year}
                                    onChange={e => handleYearChange(e.target.value)}
                                >
                                    <option value="">年</option>
                                    {yearOptions.map(y => (
                                        <option key={y} value={String(y)}>{y}</option>
                                    ))}
                                </select>
                                <SelectArrow />
                            </div>
                            <div className={styles.selectWrapper}>
                                <select
                                    className={styles.monthSelect}
                                    value={month}
                                    onChange={e => handleMonthChange(e.target.value)}
                                >
                                    <option value="">月</option>
                                    {monthOptions.map(m => (
                                        <option key={m} value={String(m)}>{m}</option>
                                    ))}
                                </select>
                                <SelectArrow />
                            </div>
                        </div>
                        {hasInteracted && birthdayError && (
                            <p className={styles.errorMessages}>{birthdayError}</p>
                        )}
                    </div>

                    <div className={styles.birthdayGenderSection}>
                        <p className={styles.sectionLabel}>性別</p>
                        <div className={styles.selectWrapper}>
                            <select
                                className={styles.sexSelect}
                                value={sex}
                                onChange={e => setSex(e.target.value)}
                            >
                                <option value="">選択</option>
                                <option value="man">男性</option>
                                <option value="woman">女性</option>
                                <option value="other">その他</option>
                            </select>
                            <SelectArrow />
                        </div>
                    </div>
                </div>

                <div className={styles.nextButtonWrapper}>
                <NextButton
                    formKey={[
                        'firstName',
                        'lastName',
                        'firstNameKana',
                        'lastNameKana',
                        'birthday',
                        'sex',
                    ]}
                    formValue={[
                        firstName,
                        lastName,
                        firstNameKana,
                        lastNameKana,
                        birthday ?? '',
                        sex,
                    ]}
                    displayFormKey={['name', 'nameKana', 'birthday', 'sex']}
                    displayFormValue={[
                        `${lastName} ${firstName}`,
                        `${lastNameKana} ${firstNameKana}`,
                        `${year}年${month}月(${age !== null ? age + '歳' : ''})`,
                        sexDisplay,
                    ]}
                    isDisabled={!isFormValid}
                />
                </div>
            </div>
        </div>
    )
}
